import { DeleteDialog, WardDialog, ActionBar } from './components';
import { useDialogState, useRowSelection } from './hooks';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { useWard } from '@/hooks/queries/useWard';
import type { XaPhuong } from '@/types/schemas';
import { WardFormattedData } from './schemas';

export default function DanhMucXaPhuong({ initialRows = [] }: { initialRows?: XaPhuong[] }) {
  const { wards, isLoading, addWard, updateWard, deleteWard, refreshWards } = useWard();
  const { clearSelection, handleRowClick, selectedObj, selectedRowIndex } = useRowSelection();
  const {
    showAddDialog,
    showEditDialog,
    showDeleteDialog,
    showWatchDialog,
    isCopyMode,

    openAddDialog,
    closeAddDialog,
    openEditDialog,
    closeEditDialog,
    openDeleteDialog,
    closeDeleteDialog,
    openWatchDialog,
    closeWatchDialog,

    handleAddButtonClick,
    handleEditButtonClick,
    handleDeleteButtonClick,
    handleCopyButtonClick
  } = useDialogState(clearSelection);

  const wardsWithId = Array.isArray(wards)
    ? wards.map(ward => ({
        ...ward,
        id: ward.uuid,
        provinceCode: ward.tinh_thanh?.ma_tinh,
        provinceName: ward.tinh_thanh?.ten_tinh,
        districtCode: ward.quan_huyen?.ma_quan,
        districtName: ward.quan_huyen?.ten_quan
      }))
    : [];

  const handleSubmit = async (data: WardFormattedData) => {
    if ((showAddDialog && !isCopyMode) || (showAddDialog && isCopyMode)) {
      await addWard({
        ma_xp: data.ma_xp,
        ten_xp: data.ten_xp,
        ten_xp2: data.ten_xp2,
        ma_tinh: data.ma_tinh,
        ma_quan: data.ma_quan,
        status: String(data.status),
        entity_model: data.entity_model || ''
      });
      closeAddDialog();
    } else if (showEditDialog && selectedObj) {
      console.log('Updating ward with UUID:', selectedObj.uuid);
      await updateWard(selectedObj.uuid, {
        ma_xp: data.ma_xp,
        ten_xp: data.ten_xp,
        ten_xp2: data.ten_xp2,
        ma_tinh: data.ma_tinh,
        ma_quan: data.ma_quan,
        status: String(data.status),
        entity_model: data.entity_model || ''
      });
      closeEditDialog();
    }
    refreshWards();
  };

  const tables = [
    {
      name: 'Danh mục xã/phường',
      rows: wardsWithId,
      columns: getDataTableColumns()
    }
  ];

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      {(showAddDialog || showEditDialog) && (
        <WardDialog
          open={showAddDialog || showEditDialog}
          mode={showEditDialog ? 'edit' : 'add'}
          initialData={
            selectedObj && showEditDialog
              ? {
                  ma_xp: selectedObj.ma_xp,
                  ten_xp: selectedObj.ten_xp,
                  ten_xp2: selectedObj.ten_xp2,
                  ma_tinh: selectedObj.ma_tinh,
                  ma_quan: selectedObj.ma_quan,
                  status: selectedObj.status,
                  uuid: selectedObj.uuid
                }
              : selectedObj && showAddDialog && isCopyMode
                ? {
                    ma_xp: selectedObj.ma_xp,
                    ten_xp: selectedObj.ten_xp,
                    ten_xp2: selectedObj.ten_xp2,
                    ma_tinh: selectedObj.ma_tinh,
                    ma_quan: selectedObj.ma_quan,
                    status: selectedObj.status,
                    uuid: selectedObj.uuid
                  }
                : {}
          }
          onClose={showEditDialog ? closeEditDialog : closeAddDialog}
          onSubmit={handleSubmit}
        />
      )}

      {showDeleteDialog && (
        <DeleteDialog
          open={showDeleteDialog}
          onClose={closeDeleteDialog}
          selectedObj={selectedObj}
          onDelete={deleteWard}
          clearSelection={clearSelection}
        />
      )}

      {showWatchDialog && selectedObj && (
        <WardDialog
          open={showWatchDialog}
          mode='view'
          initialData={{
            ma_xp: selectedObj.ma_xp,
            ten_xp: selectedObj.ten_xp,
            ten_xp2: selectedObj.ten_xp2,
            ma_tinh: selectedObj.ma_tinh,
            ma_quan: selectedObj.ma_quan,
            status: selectedObj.status,
            uuid: selectedObj.uuid
          }}
          onClose={closeWatchDialog}
          onSubmit={handleSubmit}
          onAddButtonClick={handleAddButtonClick}
          onEditButtonClick={handleEditButtonClick}
          onDeleteButtonClick={handleDeleteButtonClick}
          onCopyButtonClick={handleCopyButtonClick}
        />
      )}

      <div className='w-full'>
        <ActionBar
          onAddIconClick={openAddDialog}
          onEditIconClick={() => selectedObj && openEditDialog()}
          onDeleteIconClick={() => selectedObj && openDeleteDialog()}
          onCopyIconClick={() => selectedObj && handleCopyButtonClick()}
          onWatchIconClick={() => selectedObj && openWatchDialog()}
          onRefreshClick={refreshWards}
        />

        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex || undefined}
          onRowClick={handleRowClick}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
